import React from 'react';
import styled from '@emotion/styled';
import { AlertTriangle, Lightbulb } from 'lucide-react';

function hexToRgba(hex, alpha = 0.18) {
  if (typeof hex !== 'string') return `rgba(255,255,255,${alpha})`;
  const cleaned = hex.trim().replace('#', '');
  if (cleaned.length !== 6) return `rgba(255,255,255,${alpha})`;
  const r = parseInt(cleaned.slice(0, 2), 16);
  const g = parseInt(cleaned.slice(2, 4), 16);
  const b = parseInt(cleaned.slice(4, 6), 16);
  if ([r, g, b].some((v) => Number.isNaN(v))) return `rgba(255,255,255,${alpha})`;
  return `rgba(${r},${g},${b},${alpha})`;
}

const Card = styled.div`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.25s ease;
  border: 2px solid rgba(255, 255, 255, 0.1);
  background: #110D16;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(255, 255, 255, 0.25);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
`;

const ImageArea = styled.div`
  width: 100%;
  aspect-ratio: 1;
  background: ${props => props.$imageUrl
    ? `url(${props.$imageUrl}) center/cover no-repeat`
    : `linear-gradient(135deg, ${props.$color || '#367ABA'}33 0%, ${props.$color || '#367ABA'}11 100%)`
  };

  html[data-platform='tizen'] & {
    background: ${props => props.$imageUrl
      ? `url(${props.$imageUrl}) center/cover no-repeat`
      : `${props.$color || '#367ABA'}22`
    };
  }
`;

const Content = styled.div`
  padding: 14px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const Name = styled.h3`
  margin: 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: #fff;
  line-height: 1.2;
`;

const Counts = styled.div`
  display: flex;
  gap: 10px;
  font-size: 1.05rem;
  font-weight: 600;
  flex-shrink: 0;
`;

const CountBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${props => props.$color};
  padding: 4px 10px;
  border-radius: 999px;
  background: ${props => hexToRgba(props.$color, 0.18)};
  border: 1px solid ${props => hexToRgba(props.$color, 0.35)};
`;

/**
 * LensCard - A clickable card representing an analytical lens.
 * Shows cube face image with lens name underneath.
 */
export default function LensCard({
  name,
  imageUrl,
  color = '#367ABA',
  obstacleCount = 0,
  opportunityCount = 0,
  onClick,
}) {
  const hasCounts = obstacleCount > 0 || opportunityCount > 0;

  return (
    <Card
      role="button"
      tabIndex={0}
      aria-label={name}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
    >
      <ImageArea $imageUrl={imageUrl} $color={color} />
      <Content>
        <Name>{name}</Name>
        {hasCounts && (
          <Counts>
            {obstacleCount > 0 && (
              <CountBadge $color="#E94B6E">
                <AlertTriangle size={18} />
                {obstacleCount}
              </CountBadge>
            )}
            {opportunityCount > 0 && (
              <CountBadge $color="#A6D64D">
                <Lightbulb size={18} />
                {opportunityCount}
              </CountBadge>
            )}
          </Counts>
        )}
      </Content>
    </Card>
  );
}
